import React, { useEffect, useState } from "react";

import { PREFECTURES } from "constants/prefecture";
import { isInboundEnabled } from "util/featureFlag";

import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useGetZipcodeHook, usePatchPatientHook } from "api-client";
import { WarningIcon } from "components/atoms/Icon/ErrorIcon";
import { RadioGroup } from "components/atoms/RadioGroup";
import { Select } from "components/atoms/Select";
import { TextField } from "components/atoms/TextField";
import { TextFieldWithBirthDay } from "components/atoms/TextFieldWithBirthDay";
import { Stepper } from "components/molecules/Stepper";
import { useLocalStorage } from "hooks/useLocalStorage";
import { theme } from "providers/ThemeProviderWrap";
import { Control, Controller, FieldErrors, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { UserRegisterSchemaType } from "schema/userRegister";

type Props = {
  control: Control<{
    address: string;
    name: string;
    nameKana: string;
    email: string;
    postalCode: string;
    prefecture: string;
    phoneNumber: string;
    hasInsuranceOrMyNumber?: number;
    gender: "MALE" | "FEMALE";
    birthDay: string;
  }>;
  errors: FieldErrors<{
    address: string;
    name: string;
    nameKana: string;
    email: string;
    postalCode: string;
    prefecture: string;
    phoneNumber: string;
    hasInsuranceOrMyNumber?: number;
    gender: string;
    birthDay: string;
  }>;
  onSubmit: () => void;
  goBack: () => void;
  disabled: boolean;
  loading: boolean;
  setValue: (name: keyof UserRegisterSchemaType, value: string) => void;
  getValues: (name: keyof UserRegisterSchemaType) => string;
};

export const Presenter: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { control, errors, onSubmit, disabled, loading, setValue, getValues } =
    props;

  const [tooltipContent, setTooltipContent] = useState<React.ReactNode>(null);
  const [focus, setFocus] = useState(false);
  const getZipcode = useGetZipcodeHook();
  const [prefecture, setPrefecture] = useState<string | undefined>(undefined);
  const [isDeviationError, setIsDeviationError] = useState(false);
  const [isPostalCodeReentered, setIsPostalCodeReentered] = useState(false);
  const [isPostalCodeNotFound, setIsPostalCodeNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const usePatchPatient = usePatchPatientHook();
  const [patientId] = useLocalStorage!("patientId");

  const patchPatient = async (
    utmSource: string,
    utmMedium: string,
    utmCampaign: string,
    couponCode: string
  ) => {
    await usePatchPatient(patientId!, {
      utmList: [
        {
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign,
          coupon_code: couponCode,
        },
      ],
    });
  };

  const GENDER = [
    {
      label: t("common.gender.male"),
      value: "MALE",
    },
    {
      label: t("common.gender.female"),
      value: "FEMALE",
    },
  ];

  const HAS_INSURANCE_OR_MY_NUMBER = [
    {
      label: t("userInformation.yes"),
      value: 1,
    },
    {
      label: t("userInformation.no"),
      value: 0,
    },
  ];

  const currentPrefecture = useWatch({
    control: control,
    name: "prefecture",
  });

  const currentPostalCode = useWatch({
    control: control,
    name: "postalCode",
  });

  const currentBirthDay = useWatch({
    control: control,
    name: "birthDay",
  });

  useEffect(() => {
    console.log("###currentBirthDay", currentBirthDay);
  }, [currentBirthDay]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const utmSource = searchParams.get("utm_source");
    const utmMedium = searchParams.get("utm_medium");
    const utmCampaign = searchParams.get("utm_campaign");
    const couponCode = searchParams.get("coupon_code");

    if (utmSource && utmMedium && utmCampaign && couponCode) {
      patchPatient(utmSource, utmMedium, utmCampaign, couponCode);
    }
    // eslint-disable-next-line
  }, []);

  const updateAddressFields = (
    prefecture: string,
    city: string,
    town: string
  ) => {
    setPrefecture(prefecture);
    setValue("prefecture", prefecture);

    // 現在の住所を取得
    const currentAddress = getValues("address");
    // 住所が未入力の場合のみ設定
    if (!currentAddress || isPostalCodeReentered) {
      setValue("address", city + town);
    }

    // 選択後はTooltipを閉じる
    setFocus(false);
    setTooltipContent(null);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlePostalCodeChange = async (v: string) => {
    const postalCode = v;
    setIsLoading(true);
    // 郵便番号が変更されたらエラー状態をリセット
    setIsPostalCodeNotFound(false);

    if (postalCode.length === 7) {
      try {
        const postalData = await getZipcode({ zipcode: v });

        // 郵便番号が入力された時点で再入力フラグを立てる
        setIsPostalCodeReentered(true);

        if (postalData && postalData.results && postalData.results.length > 0) {
          setIsPostalCodeNotFound(false);
          if (postalData.results.length > 1) {
            setFocus(true);
            setTooltipContent(
              // TODO: 共通化できるようコンポーネントとして切り出す
              <List>
                {postalData.results.map((result, index) => (
                  <ListItem key={index}>
                    <ListItemButton
                      onClick={() => {
                        updateAddressFields(
                          result.prefecture,
                          result.city,
                          result.town
                        );
                      }}
                    >
                      {result.prefecture} {result.city} {result.town}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            );
          } else {
            const res = postalData.results[0];
            updateAddressFields(res.prefecture, res.city, res.town);
          }
        } else {
          // 検索結果がない場合はエラー状態を設定
          setIsPostalCodeNotFound(true);
          updateAddressFields("", "", "");
        }
      } catch (e) {
        console.error(e);
        // API呼び出しエラーの場合もエラー状態を設定
        setIsPostalCodeNotFound(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const [initPrefecture, setInitPrefecture] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    const fetchPostalData = async () => {
      const postalCode = getValues("postalCode");
      if (postalCode.length === 7) {
        try {
          const postalData = await getZipcode({ zipcode: postalCode });
          console.log("postalData:", postalData);
          console.log(
            "postalData.results[0].prefecture:",
            postalData.results[0].prefecture
          );
          setInitPrefecture(postalData.results[0].prefecture);
        } catch (e) {
          console.error(e);
        }
      }
    };

    fetchPostalData();
  }, [getValues, getZipcode]);

  useEffect(() => {
    const inputPostalCode = getValues("postalCode");
    const inputPrefecture = getValues("prefecture");
    setIsDeviationError(
      !!inputPostalCode && !!inputPrefecture && prefecture !== inputPrefecture
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPostalCode,
    currentPrefecture,
    getValues,
    initPrefecture,
    prefecture,
  ]);

  return (
    <Box>
      <Box padding={2}>
        <Stepper activeStep={0} />
      </Box>
      <Typography variant="h2" textAlign="center" mb={2}>
        {t("userRegister.title")}
      </Typography>
      {isDeviationError && (
        <Stack width="100%" alignItems="center">
          <Stack
            width="268px"
            direction="row"
            alignItems="flex-start"
            spacing={0.5}
            marginTop={1}
            py={0.5}
            px={1}
            bgcolor="#FFF7E6"
            sx={{ borderRadius: "6px" }}
          >
            <Stack width="12px" pt={0.5}>
              <WarningIcon />
            </Stack>
            <Typography variant="caption" color={theme.palette.warning.main}>
              {t("userRegister.addressError")}
            </Typography>
          </Stack>
        </Stack>
      )}
      <form onSubmit={onSubmit}>
        <Stack direction="column" spacing={2} justifyContent="center">
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  label={t("userRegister.name.label")}
                  type="text"
                  required
                  placeholder={t("userRegister.name.placeholder")}
                  error={!!errors["name"]}
                  errorText={errors["name"]?.message}
                  {...field}
                />
                <Box>
                  <Typography variant="caption" color="error.main">
                    {t("userRegister.name.officialDocumentEntryNotice")}
                  </Typography>
                  <List dense sx={{ color: "error.main", p: 0 }}>
                    <ListItem alignItems="flex-start" sx={{ p: 0 }}>
                      <Typography variant="caption" sx={{ mr: 0.5 }}>
                        ※
                      </Typography>
                      <ListItemText
                        primary={t("userRegister.name.informationMismatchNote")}
                        primaryTypographyProps={{
                          variant: "caption",
                          lineHeight: 1.5,
                        }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </>
            )}
          />
          <Controller
            name="nameKana"
            control={control}
            render={({ field }) => (
              <TextField
                label={t("userRegister.nameKana.label")}
                type="text"
                placeholder={t("userRegister.nameKana.placeholder")}
                required
                error={!!errors["nameKana"]}
                errorText={errors["nameKana"]?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="birthDay"
            control={control}
            defaultValue={currentBirthDay ? currentBirthDay : "1980-01-01"}
            render={({ field }) => (
              <TextFieldWithBirthDay
                label={t("userRegister.birthDay.label")}
                type="date"
                required
                error={!!errors["birthDay"]}
                errorText={errors["birthDay"]?.message}
                {...field}
                defaultValue={currentBirthDay ? currentBirthDay : null}
              />
            )}
          />
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <RadioGroup
                sx={{ width: "50%" }}
                groupLabel={t("userRegister.gender.label")}
                options={GENDER}
                required
                error={!!errors["gender"]}
                errorText={errors["gender"]?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                label={t("userRegister.email.label")}
                type="email"
                required={false}
                disabled={true}
                placeholder="username@gmail.com"
                error={!!errors["email"]}
                errorText={errors["email"]?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="postalCode"
            control={control}
            render={({ field }) => (
              <div
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
              >
                <Tooltip title={tooltipContent} open={focus} placement="top">
                  <>
                    <TextField
                      sx={{ width: "30%" }}
                      label={t("userRegister.postalCode.label")}
                      type="text"
                      required
                      placeholder="1234567"
                      error={!!errors["postalCode"]}
                      errorText={errors["postalCode"]?.message}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        handlePostalCodeChange(e.target.value);
                      }}
                    />
                    <FormHelperText>
                      {t("userRegister.postalCode.helper")}
                    </FormHelperText>
                    {isDeviationError && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        marginTop={1}
                        py={0.5}
                        px={1}
                        bgcolor="#FFF7E6"
                        sx={{ borderRadius: "6px" }}
                      >
                        <WarningIcon />
                        <Typography
                          variant="caption"
                          color={theme.palette.warning.main}
                        >
                          {t(
                            "common.message.postalCodeAndPrefectureDoNotMatch"
                          )}
                        </Typography>
                      </Stack>
                    )}
                    {isPostalCodeNotFound && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        marginTop={1}
                        py={0.5}
                        px={1}
                        bgcolor="#FFF7E6"
                        sx={{ borderRadius: "6px" }}
                      >
                        <WarningIcon />
                        <Typography
                          variant="caption"
                          color={theme.palette.warning.main}
                        >
                          {t("userRegister.postalCode.notFound")}
                        </Typography>
                      </Stack>
                    )}
                  </>
                </Tooltip>
              </div>
            )}
          />
          <Box sx={{ width: "100%" }}>
            <Controller
              name="prefecture"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Select
                  label={t("userRegister.prefecture.label")}
                  type="prefecture"
                  required
                  placeholder={t("userRegister.prefecture.placeholder")}
                  options={PREFECTURES}
                  error={!!errors["prefecture"]}
                  errorText={errors["prefecture"]?.message}
                  {...field}
                  sx={{ width: "100%" }}
                />
              )}
            />
            {isDeviationError && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                marginTop={1}
                py={0.5}
                px={1}
                bgcolor="#FFF7E6"
                sx={{ borderRadius: "6px" }}
              >
                <WarningIcon />
                <Typography
                  variant="caption"
                  color={theme.palette.warning.main}
                >
                  {t("common.message.postalCodeAndPrefectureDoNotMatch")}
                </Typography>
              </Stack>
            )}
          </Box>
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                label={t("userRegister.address.label")}
                type="text"
                required
                placeholder={t("userRegister.address.placeholder")}
                error={!!errors["address"]}
                errorText={errors["address"]?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <TextField
                label={t("userRegister.phoneNumber.label")}
                type="tel"
                required
                placeholder="08012345678"
                error={!!errors["phoneNumber"]}
                errorText={errors["phoneNumber"]?.message}
                {...field}
              />
            )}
          />
          {isInboundEnabled() && (
            <Controller
              name="hasInsuranceOrMyNumber"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  sx={{ width: "50%" }}
                  groupLabel={t(
                    "userInformation.hasInsuranceOrMyNumberQuestion"
                  )}
                  options={HAS_INSURANCE_OR_MY_NUMBER}
                  required
                  error={!!errors["hasInsuranceOrMyNumber"]}
                  errorText={errors["hasInsuranceOrMyNumber"]?.message}
                  value={String(field.value ?? "")}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />
          )}
          <Box paddingY="16px">
            <Button
              variant="onboarding"
              color="primary"
              type="submit"
              sx={{ width: "100%", borderRadius: "10px", height: "48px" }}
              disabled={disabled || isLoading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {t("common.button.next")}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
};
